"use strict";
/**
 * User Management Service
 * Core business logic for user operations
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.userManagementService = exports.UserManagementService = void 0;
const userValidation_1 = require("../../rules/userValidation");
/**
 * User Management Service Class
 */
class UserManagementService {
    /**
     * Validate user registration data
     */
    validateUserRegistration(request) {
        return (0, userValidation_1.validateUserRegistration)({
            email: request.email,
            username: request.username,
            password: request.password,
            nativeLanguage: request.nativeLanguage,
            learningLanguages: request.learningLanguages,
            proficiencyLevel: request.proficiencyLevel,
        });
    }
    /**
     * Validate profile update
     */
    validateProfileUpdate(request) {
        return (0, userValidation_1.validateUserProfileUpdate)({
            email: request.email,
            username: request.username,
            nativeLanguage: request.nativeLanguage,
            learningLanguages: request.targetLanguage
                ? [request.targetLanguage]
                : undefined,
            proficiencyLevel: request.level,
        });
    }
    /**
     * Validate preferences update
     */
    validatePreferencesUpdate(request) {
        return (0, userValidation_1.validateUserPreferences)(request.nativeLanguage, request.learningLanguages, request.proficiencyLevel);
    }
    /**
     * Generate user profile
     */
    generateUserProfile(user, preferences) {
        return {
            id: user.id,
            email: user.email,
            username: user.username || "",
            firstName: user.firstName,
            lastName: user.lastName,
            nativeLanguage: user.nativeLanguage,
            targetLanguage: user.targetLanguage,
            level: user.level,
            timezone: user.timezone,
            isEmailVerified: user.isEmailVerified || false,
            isAnonymous: user.isAnonymous,
            createdAt: user.createdAt,
            updatedAt: user.updatedAt,
            // Add preferences if provided
            preferences: preferences
                ? {
                    studyReminders: preferences.studyReminders,
                    emailNotifications: preferences.emailNotifications,
                    soundEnabled: preferences.soundEnabled,
                    theme: preferences.theme,
                }
                : undefined,
        };
    }
    /**
     * Calculate user level progression
     */
    calculateLevelProgression(currentLevel, studyStats) {
        const levelOrder = [
            "BEGINNER",
            "ELEMENTARY",
            "INTERMEDIATE",
            "UPPER_INTERMEDIATE",
            "ADVANCED",
            "PROFICIENT",
        ];
        const currentIndex = levelOrder.indexOf(currentLevel);
        const nextLevel = currentIndex < levelOrder.length - 1
            ? levelOrder[currentIndex + 1]
            : undefined;
        // Define requirements for each level advancement
        const requirements = {
            BEGINNER: {
                minTextsCompleted: 0,
                minDictationsCompleted: 0,
                minAverageAccuracy: 0,
                minStudyTime: 0,
                minStreak: 0,
            },
            ELEMENTARY: {
                minTextsCompleted: 10,
                minDictationsCompleted: 5,
                minAverageAccuracy: 0.7,
                minStudyTime: 300,
                minStreak: 3,
            },
            INTERMEDIATE: {
                minTextsCompleted: 25,
                minDictationsCompleted: 15,
                minAverageAccuracy: 0.75,
                minStudyTime: 600,
                minStreak: 7,
            },
            UPPER_INTERMEDIATE: {
                minTextsCompleted: 50,
                minDictationsCompleted: 30,
                minAverageAccuracy: 0.8,
                minStudyTime: 1200,
                minStreak: 14,
            },
            ADVANCED: {
                minTextsCompleted: 100,
                minDictationsCompleted: 60,
                minAverageAccuracy: 0.85,
                minStudyTime: 2400,
                minStreak: 21,
            },
            PROFICIENT: {
                minTextsCompleted: 200,
                minDictationsCompleted: 120,
                minAverageAccuracy: 0.9,
                minStudyTime: 4800,
                minStreak: 30,
            },
        };
        if (!nextLevel) {
            return {
                currentLevel,
                progress: 100,
                requirementsForNext: [],
                canAdvance: false,
            };
        }
        const nextRequirements = requirements[nextLevel];
        const requirementsForNext = [];
        let met = 0;
        const total = 5; // Total number of requirements
        // Check each requirement
        if ((studyStats.textsCompleted || 0) < nextRequirements.minTextsCompleted) {
            requirementsForNext.push(`Complete ${nextRequirements.minTextsCompleted} texts (${studyStats.textsCompleted || 0}/${nextRequirements.minTextsCompleted})`);
        }
        else {
            met++;
        }
        if ((studyStats.dictationsCompleted || 0) <
            nextRequirements.minDictationsCompleted) {
            requirementsForNext.push(`Complete ${nextRequirements.minDictationsCompleted} dictations (${studyStats.dictationsCompleted || 0}/${nextRequirements.minDictationsCompleted})`);
        }
        else {
            met++;
        }
        if ((studyStats.averageAccuracy || 0) < nextRequirements.minAverageAccuracy) {
            requirementsForNext.push(`Achieve ${Math.round(nextRequirements.minAverageAccuracy * 100)}% average accuracy (${Math.round((studyStats.averageAccuracy || 0) * 100)}%)`);
        }
        else {
            met++;
        }
        if ((studyStats.totalStudyTime || 0) < nextRequirements.minStudyTime) {
            requirementsForNext.push(`Study for ${nextRequirements.minStudyTime} minutes (${studyStats.totalStudyTime || 0}/${nextRequirements.minStudyTime})`);
        }
        else {
            met++;
        }
        if ((studyStats.currentStreak || 0) < nextRequirements.minStreak) {
            requirementsForNext.push(`Maintain ${nextRequirements.minStreak}-day study streak (${studyStats.currentStreak || 0}/${nextRequirements.minStreak})`);
        }
        else {
            met++;
        }
        const progress = Math.round((met / total) * 100);
        const canAdvance = met === total;
        return {
            currentLevel,
            progress,
            nextLevel,
            requirementsForNext,
            canAdvance,
        };
    }
    /**
     * Suggest learning path
     */
    suggestLearningPath(userLevel, nativeLanguage, targetLanguage, studyStats) {
        // Base recommendations on current level
        const recommendations = {
            recommendedTexts: [],
            recommendedDictations: [],
            studyPlan: {
                dailyGoal: 15, // Default 15 minutes
                weeklyGoal: 105, // 7 * 15
                focusAreas: [],
            },
        };
        // Adjust based on level
        switch (userLevel) {
            case "BEGINNER":
                recommendations.recommendedTexts.push({ level: "BEGINNER", textSize: "SHORT", estimatedTime: 5 }, { level: "BEGINNER", textSize: "SHORT", estimatedTime: 5 });
                recommendations.recommendedDictations.push({
                    difficulty: "EASY",
                    estimatedTime: 10,
                });
                recommendations.studyPlan.focusAreas = [
                    "Basic vocabulary",
                    "Simple sentences",
                    "Pronunciation",
                ];
                break;
            case "ELEMENTARY":
                recommendations.recommendedTexts.push({ level: "ELEMENTARY", textSize: "SHORT", estimatedTime: 8 }, { level: "ELEMENTARY", textSize: "MEDIUM", estimatedTime: 12 });
                recommendations.recommendedDictations.push({
                    difficulty: "EASY",
                    estimatedTime: 12,
                });
                recommendations.studyPlan.focusAreas = [
                    "Common phrases",
                    "Simple grammar",
                    "Listening skills",
                ];
                break;
            case "INTERMEDIATE":
                recommendations.recommendedTexts.push({ level: "INTERMEDIATE", textSize: "MEDIUM", estimatedTime: 15 }, { level: "INTERMEDIATE", textSize: "LONG", estimatedTime: 20 });
                recommendations.recommendedDictations.push({
                    difficulty: "MEDIUM",
                    estimatedTime: 15,
                });
                recommendations.studyPlan.dailyGoal = 20;
                recommendations.studyPlan.weeklyGoal = 140;
                recommendations.studyPlan.focusAreas = [
                    "Complex sentences",
                    "Idioms",
                    "Fluency",
                ];
                break;
            case "UPPER_INTERMEDIATE":
                recommendations.recommendedTexts.push({
                    level: "UPPER_INTERMEDIATE",
                    textSize: "MEDIUM",
                    estimatedTime: 18,
                }, { level: "UPPER_INTERMEDIATE", textSize: "LONG", estimatedTime: 25 });
                recommendations.recommendedDictations.push({
                    difficulty: "MEDIUM",
                    estimatedTime: 18,
                });
                recommendations.studyPlan.dailyGoal = 25;
                recommendations.studyPlan.weeklyGoal = 175;
                recommendations.studyPlan.focusAreas = [
                    "Advanced grammar",
                    "Cultural context",
                    "Speed",
                ];
                break;
            case "ADVANCED":
                recommendations.recommendedTexts.push({
                    level: "ADVANCED",
                    textSize: "LONG",
                    estimatedTime: 30,
                });
                recommendations.recommendedDictations.push({
                    difficulty: "HARD",
                    estimatedTime: 25,
                });
                recommendations.studyPlan.dailyGoal = 30;
                recommendations.studyPlan.weeklyGoal = 210;
                recommendations.studyPlan.focusAreas = [
                    "Professional language",
                    "Complex topics",
                    "Perfect accuracy",
                ];
                break;
            case "PROFICIENT":
                recommendations.recommendedTexts.push({
                    level: "PROFICIENT",
                    textSize: "LONG",
                    estimatedTime: 35,
                });
                recommendations.recommendedDictations.push({
                    difficulty: "HARD",
                    estimatedTime: 30,
                });
                recommendations.studyPlan.dailyGoal = 35;
                recommendations.studyPlan.weeklyGoal = 245;
                recommendations.studyPlan.focusAreas = [
                    "Native-like fluency",
                    "Specialized vocabulary",
                    "Cultural nuances",
                ];
                break;
        }
        // Adjust based on performance
        if (studyStats?.averageAccuracy && studyStats.averageAccuracy > 0.9) {
            // High accuracy - suggest more challenging content
            recommendations.recommendedTexts.forEach((text) => {
                if (text.level !== "PROFICIENT") {
                    const levelIndex = [
                        "BEGINNER",
                        "ELEMENTARY",
                        "INTERMEDIATE",
                        "UPPER_INTERMEDIATE",
                        "ADVANCED",
                        "PROFICIENT",
                    ].indexOf(text.level);
                    text.level = [
                        "BEGINNER",
                        "ELEMENTARY",
                        "INTERMEDIATE",
                        "UPPER_INTERMEDIATE",
                        "ADVANCED",
                        "PROFICIENT",
                    ][levelIndex + 1];
                }
            });
        }
        return recommendations;
    }
    /**
     * Calculate study streak
     */
    calculateStudyStreak(studyDates) {
        if (studyDates.length === 0) {
            return {
                currentStreak: 0,
                longestStreak: 0,
                streakStatus: "none",
            };
        }
        // Sort dates in descending order
        const sortedDates = studyDates
            .map((date) => new Date(date))
            .sort((a, b) => b.getTime() - a.getTime());
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const lastStudyDate = sortedDates[0];
        lastStudyDate.setHours(0, 0, 0, 0);
        // Check if streak is active (studied today or yesterday)
        const daysSinceLastStudy = Math.floor((today.getTime() - lastStudyDate.getTime()) / (1000 * 60 * 60 * 24));
        const streakStatus = daysSinceLastStudy <= 1 ? "active" : "broken";
        // Calculate current streak
        let currentStreak = 0;
        const currentDate = new Date(today);
        if (daysSinceLastStudy === 0 || daysSinceLastStudy === 1) {
            for (const studyDate of sortedDates) {
                studyDate.setHours(0, 0, 0, 0);
                if (Math.abs(currentDate.getTime() - studyDate.getTime()) <=
                    1000 * 60 * 60 * 24) {
                    currentStreak++;
                    currentDate.setDate(currentDate.getDate() - 1);
                }
                else {
                    break;
                }
            }
        }
        // Calculate longest streak
        let longestStreak = 0;
        let tempStreak = 1;
        for (let i = 1; i < sortedDates.length; i++) {
            const prevDate = new Date(sortedDates[i - 1]);
            const currDate = new Date(sortedDates[i]);
            prevDate.setHours(0, 0, 0, 0);
            currDate.setHours(0, 0, 0, 0);
            const daysDiff = Math.floor((prevDate.getTime() - currDate.getTime()) / (1000 * 60 * 60 * 24));
            if (daysDiff === 1) {
                tempStreak++;
            }
            else {
                longestStreak = Math.max(longestStreak, tempStreak);
                tempStreak = 1;
            }
        }
        longestStreak = Math.max(longestStreak, tempStreak);
        return {
            currentStreak,
            longestStreak,
            lastStudyDate: sortedDates[0],
            streakStatus,
        };
    }
}
exports.UserManagementService = UserManagementService;
// Create singleton instance
exports.userManagementService = new UserManagementService();
//# sourceMappingURL=userManagementService.js.map